import { Injectable } from '@angular/core';
import { EncryptedEnvelope, GeneratedKeyPair } from './encryption.types';

@Injectable({ providedIn: 'root' })
export class BrowserEncryptionService {
  private static readonly KEK_ALGORITHM = 'RSA-OAEP';
  private static readonly DEK_ALGORITHM = 'AES-GCM';
  private static readonly DEK_LENGTH = 256;
  private static readonly EXPECTED_ALGORITHM = 'RSA-OAEP+AES-256-GCM';
  private static readonly GCM_TAG_LENGTH = 128;

  async generateKeyPair(): Promise<GeneratedKeyPair> {
    const keyPair = await crypto.subtle.generateKey(
      {
        name: BrowserEncryptionService.KEK_ALGORITHM,
        // Note: 2048-bit pending security team decision on RSA-4096
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-256',
      },
      false,
      ['wrapKey', 'unwrapKey']
    );

    const jwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
    return {
      publicKeyBase64: btoa(JSON.stringify(jwk)),
      keyPair,
    };
  }

  async decrypt(envelope: EncryptedEnvelope, privateKey: CryptoKey): Promise<string> {
    if (envelope.algorithm !== BrowserEncryptionService.EXPECTED_ALGORITHM) {
      throw new Error(`Unsupported encryption algorithm:` + ` ${envelope.algorithm}`);
    }

    const encryptedDataKeyBuf = this.base64ToBuffer(envelope.encryptedDataKey);
    const ivBuf = this.base64ToBuffer(envelope.iv);
    const ciphertextBuf = this.base64ToBuffer(envelope.ciphertext);

    const dek = await crypto.subtle.unwrapKey(
      'raw',
      encryptedDataKeyBuf,
      privateKey,
      BrowserEncryptionService.KEK_ALGORITHM,
      {
        name: BrowserEncryptionService.DEK_ALGORITHM,
        length: BrowserEncryptionService.DEK_LENGTH,
      },
      false,
      ['decrypt']
    );

    const plaintext = await crypto.subtle.decrypt(
      {
        name: BrowserEncryptionService.DEK_ALGORITHM,
        iv: ivBuf,
        tagLength: BrowserEncryptionService.GCM_TAG_LENGTH,
      },
      dek,
      ciphertextBuf
    );

    return new TextDecoder().decode(plaintext);
  }

  private base64ToBuffer(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
